import { CommonModule } from "@angular/common";
import { Component, DOCUMENT, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { PaymentResponsePixData } from "@models/payment";
import { ConfirmActionModalComponent, ConfirmDialogData } from "../../../../shared/modals/confirm-action-modal/confirm-action-modal.component";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { SnackBarService } from "@services/snack-bar.service";
import { QrCodeModule } from "ng-qrcode";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { BehaviorSubject, Observable, take } from "rxjs";

export interface SellerPaymentPendingDialogData {
  mode: "pix" | "payment_link";
  pixData: PaymentResponsePixData | null;
  paymentLinkUrl: string | null;
  state$: BehaviorSubject<{
    statusText: string;
    isWebSocketConnected: boolean;
  }>;
}

@Component({
  selector: "app-seller-payment-pending-dialog",
  imports: [
    CommonModule,
    MatDialogModule,
    ConfirmActionModalComponent,
    QrCodeModule,
    ButtonComponent,
    ProgressSpinnerModule,
  ],
  templateUrl: "./seller-payment-pending-dialog.component.html",
  styleUrl: "./seller-payment-pending-dialog.component.scss",
})
export class SellerPaymentPendingDialogComponent {
  private readonly document = inject(DOCUMENT);
  private readonly matDialog = inject(MatDialog);
  private readonly snackbarService = inject(SnackBarService);
  private readonly data = inject<SellerPaymentPendingDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<SellerPaymentPendingDialogComponent>);

  readonly mode = this.data.mode;
  readonly pixData = this.data.pixData;
  readonly paymentLinkUrl = this.data.paymentLinkUrl;
  readonly state$: Observable<{
    statusText: string;
    isWebSocketConnected: boolean;
  }> = this.data.state$.asObservable();

  onRequestClose(): void {
    this.matDialog
      .open(ConfirmActionModalComponent, {
        width: "min(100vw - 2rem, 26rem)",
        maxWidth: "100vw",
        data: {
          modalTitle: "Fechar painel de cobrança",
          modalContent: `Tem certeza que deseja fechar? Se o pagamento for confirmado, o pedido aparecerá em <strong>Minhas vendas</strong>.`,
          confirmMessage: "Sim, fechar",
          cancelMessage: "Não, continuar",
        } as ConfirmDialogData,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.dialogRef.close("dismissed");
        }
      });
  }

  copyPixCode(): void {
    if (!this.pixData?.qr_code) return;
    const input = this.document.createElement("input");
    input.style.position = "fixed";
    input.style.opacity = "0";
    input.value = this.pixData.qr_code;
    this.document.body.appendChild(input);
    input.select();
    input.setSelectionRange(0, 99999);
    this.document.execCommand("copy");
    this.document.body.removeChild(input);
    this.snackbarService.openSnackBar("Código PIX copiado.", "Fechar", "success");
  }

  copyLink(): void {
    if (!this.paymentLinkUrl) return;
    const input = this.document.createElement("input");
    input.style.position = "fixed";
    input.style.opacity = "0";
    input.value = this.paymentLinkUrl;
    this.document.body.appendChild(input);
    input.select();
    this.document.execCommand("copy");
    this.document.body.removeChild(input);
    this.snackbarService.openSnackBar("Link copiado.", "Fechar", "success");
  }

  openLinkInNewTab(): void {
    if (this.paymentLinkUrl) {
      this.document.defaultView?.open(this.paymentLinkUrl, "_blank", "noopener,noreferrer");
    }
  }
}
